import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Extensions, JSONContent } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import type { Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { useTranslation } from 'react-i18next';
import { RICH_TEXT_EXTENSIONS, toInlineTextContent } from 'components/richText';
import type { SuggestionItem } from 'types/result';
import { buildNoteDoc } from 'utils/noteDocument';
import { docToHtml, docToSuggestionIds, rebuildNote } from './noteEditor.utils';
import { NOTE_EDITOR_CLASS } from './ResultSection.styles';
import { SuggestionMark } from './suggestionMark';

export const useNoteEditor = ({
  value,
  label,
  suggestions,
  onChange,
  onFocus,
  extraExtensions,
  onKeyDown,
}: {
  value: string;
  label: string;
  suggestions: SuggestionItem[];
  onChange: (text: string) => void;
  onFocus: () => void;
  extraExtensions: Extensions;
  onKeyDown: (event: KeyboardEvent) => boolean;
}) => {
  const { t } = useTranslation();
  const placeholder = t('result.suggestions.noManualNotes');
  const pendingInsertRef = useRef<{ from: number; length: number } | null>(null);
  const knownIdsRef = useRef(new Set(suggestions.map((item) => item.id)));

  // Memoized: useEditor treats a new extensions/content reference as a real change and
  // re-reconciles the editor, so this must only change when `placeholder` or the (stable) extra extensions do.
  const extensions = useMemo(
    () => [
      ...RICH_TEXT_EXTENSIONS,
      UndoRedo,
      Placeholder.configure({ placeholder }),
      SuggestionMark,
      ...extraExtensions,
    ],
    [placeholder, extraExtensions]
  );

  const initialContentRef = useRef<JSONContent | null>(null);
  if (!initialContentRef.current) {
    initialContentRef.current = buildNoteDoc(value);
  }

  const editorProps = useMemo(
    () => ({
      attributes: {
        class: NOTE_EDITOR_CLASS,
        'data-ph-no-autocapture': 'true',
        role: 'textbox',
        'aria-label': label,
      },
      handleKeyDown: (_view: EditorView, event: KeyboardEvent) => onKeyDown(event),
    }),
    [label, onKeyDown]
  );

  const editor = useEditor({
    extensions,
    content: initialContentRef.current,
    editorProps,
    onUpdate: ({ editor }) => onChange(docToHtml(editor)),
    onFocus,
  });

  useEffect(() => {
    const knownIds = knownIdsRef.current;
    knownIdsRef.current = new Set(suggestions.map((item) => item.id));
    const docIds = new Set(docToSuggestionIds(editor));
    const gained = suggestions.some((item) => !knownIds.has(item.id) && !docIds.has(item.id));
    if (gained) rebuildNote(editor, value);
  }, [value, suggestions, editor]);

  // Remaps pendingInsertRef through every transaction, so an unrelated edit elsewhere in the
  // doc (e.g. accepting a chip) doesn't leave the next dictation chunk targeting a stale range.
  useEffect(() => {
    const handleTransaction = ({ transaction }: { transaction: Transaction }) => {
      const pending = pendingInsertRef.current;
      if (!pending) return;
      const newFrom = transaction.mapping.map(pending.from);
      const newTo = transaction.mapping.map(pending.from + pending.length);
      pendingInsertRef.current = { from: newFrom, length: Math.max(0, newTo - newFrom) };
    };
    editor.on('transaction', handleTransaction);
    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);

  const insertTextAtCaret = useCallback(
    (text: string, isFinal: boolean) => {
      const pending = pendingInsertRef.current;
      const from = pending?.from ?? editor.state.selection.from;
      const to = pending ? from + pending.length : from;

      editor.chain().insertContentAt({ from, to }, toInlineTextContent(text)).run();
      const caretPos = from + text.length;

      if (isFinal) {
        pendingInsertRef.current = null;
        editor.chain().insertContentAt(caretPos, ' ').run();
      } else {
        pendingInsertRef.current = { from, length: text.length };
      }
    },
    [editor]
  );

  const clearPendingInsert = useCallback(() => {
    pendingInsertRef.current = null;
  }, []);

  return { editor, insertTextAtCaret, clearPendingInsert };
};
